import React, { useState } from "react";
import {
  ArrayInput,
  NumberInput,
  SimpleFormIterator,
  BooleanInput,
  FormDataConsumer,
  required,
  useRecordContext,
} from "react-admin";
import { Grid, Accordion, AccordionSummary, AccordionDetails, Card, CardContent, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useFormContext, useWatch } from "react-hook-form";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import CustomTimeInput from "server/components/Fields/CustomTimeInput";

import RoomsList from "./RoomsList";
import { ExamCategories, ExamTypes } from "utils/constants";
import { settings } from "app";

const examStartDateValidate = (formData, startDateValue) => {
  if (!startDateValue) {
    return "Задължително";
  }

  const valueRegistrationTo = formData.registrationTo;
  if (valueRegistrationTo && valueRegistrationTo >= startDateValue) {
    return "Датата на изпита трябва да е след датата за край на кампания";
  }

  // const dateMap = new Map();
  // formData.exams.forEach(exam => {
  //   exam.examDays?.forEach(day => {
  //     const date = day.examStartTimestamp;
  //     if (dateMap.has(date)) {
  //       dateMap.set(date, false);
  //       return;
  //     } else {
  //       dateMap.set(date, true);
  //     }
  //   });
  // });

  // if (dateMap.has(startDateValue)) {
  //   if (!dateMap.get(startDateValue)) {
  //     return "Датите на изпитите трябва да са в различни дни";
  //   }
  // }
};

const timeSlotStartTimeValidate = (scopedFormData, examDayData) => {
  const startTime = scopedFormData?.startTime;
  if (!startTime) {
    return "Задължително";
  }
  const endTime = scopedFormData?.endTime;
  if (startTime && endTime && startTime >= endTime) {
    return "Крайната дата на изпита трябва да е по-голяма от началната";
  }

  // const examDaytimeSlots = examDayData.timeSlots;
  // if (startTime && endTime) {
  //   for (const slot of examDaytimeSlots) {
  //     if (slot === scopedFormData || !slot.startTime || !slot.endTime) {
  //       continue;
  //     }
  //     const isValidOverLap = endTime <= slot.startTime || startTime >= slot.endTime;
  //     if (!isValidOverLap) {
  //       return "Има вече избран изпит в същия часови диапазон";
  //     }
  //   }
  // }
};

const timeSlotEndTimeValidate = (scopedFormData, examDayData) => {
  const endTime = scopedFormData?.endTime;
  if (!endTime) {
    return "Задължително";
  }
  const startTime = scopedFormData?.startTime;
  if (startTime && endTime && startTime >= endTime) {
    return "Крайната дата на изпита трябва да е по-голяма от началната";
  }

  // const examDaytimeSlots = examDayData.timeSlots;
  // if (startTime && endTime) {
  //   for (const slot of examDaytimeSlots) {
  //     if (slot === scopedFormData || !slot.startTime || !slot.endTime) {
  //       continue;
  //     }
  //     const isValidOverLap = endTime <= slot.startTime || startTime >= slot.endTime;
  //     if (!isValidOverLap) {
  //       return "Има вече избран изпит в същия часови диапазон";
  //     }
  //   }
  // }
};

const timeSlotBreakHoursValidate = scopedFormData => {
  const breakStartTime = scopedFormData?.breakHourStart;
  const breakEndTime = scopedFormData?.breakHourEnd;

  if (!breakStartTime || !breakEndTime) {
    return;
  }
  if (breakStartTime >= breakEndTime) {
    return "Крайната дата на почивката трябва да е по-голяма от началната";
  }

  const startTime = scopedFormData?.startTime;
  const endTime = scopedFormData?.endTime;

  if (!startTime || !endTime) {
    return;
  }

  const validBreakTime = breakStartTime >= startTime && breakEndTime <= endTime;
  if (!validBreakTime) {
    return "Времето за почивка трябва да бъде в рамките на времето за изпита";
  }
};

const ExamsSlots = ({ examCategoryId, examIndex, examTypeId, readOnly }) => {
  const record = useRecordContext();
  const [expanded, setExpanded] = useState(false);
  const { setValue } = useFormContext();
  const exams = useWatch({ name: "exams" });

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const handleExamDaysChange = (event, index) => {
    const daysCount = Number(event.target.value) || 1;

    // Get existing examDays for the given index
    const recordExamDays = exams?.[index]?.examDays?.filter(x => x.examStartTimestamp != null) || [];

    // Create the remaining empty slots
    const newExamDays = [...recordExamDays, ...Array.from({ length: daysCount - recordExamDays.length }, () => ({}))];

    // Set it to the right exams[index].examDays
    setValue(`exams.${index}.examDays`, newExamDays);
  };

  return (
    <>
      <Grid
        container
        spacing={1}
      >
        <Grid
          item
          xs={12}
          md={2}
          sx={{ marginLeft: 3 }}
        >
          <NumberInput
            source="examDaysCount"
            label="Брой дни за изпит"
            defaultValue={1}
            onChange={event => handleExamDaysChange(event, examIndex)}
            fullWidth
            readOnly={readOnly}
          />
        </Grid>
      </Grid>
      <Grid
        container
        spacing={1}
      >
        <Grid
          item
          xs={12}
          md={12}
        >
          <ArrayInput
            label="Дни на изпита"
            key={examIndex}
            source="examDays"
            defaultValue={[{}]}
            sx={{ marginLeft: 3 }}
          >
            <SimpleFormIterator
              disableAdd
              disableReordering
              disableRemove={readOnly}
              sx={settings.style.iteratorWithoutGap}
            >
              <FormDataConsumer>
                {({ formData, scopedFormData }) => {
                  const examDayData = scopedFormData;
                  const currentExamStartTimestamp = scopedFormData?.examStartTimestamp;
                  const isExamPassed = scopedFormData?.examPassed;

                  return (
                    <Card
                      variant="outlined"
                      sx={{ mb: 2, p: 2 }}
                    >
                      <CardContent>
                        <Typography variant="h6">Избери дата за изпит</Typography>
                        <Grid
                          item
                          xs={12}
                          md={2}
                        >
                          <CustomDateInput
                            source="examStartTimestamp"
                            label="Дата на изпита"
                            required={true}
                            validate={() => examStartDateValidate(formData, currentExamStartTimestamp)}
                            readOnly={isExamPassed || readOnly}
                          />
                        </Grid>
                        {currentExamStartTimestamp && (
                          <Grid
                            item
                            xs={12}
                            md={currentExamStartTimestamp ? 12 : expanded ? 12 : 2}
                          >
                            <Accordion
                              sx={{ marginTop: 2, marginBottom: 2 }}
                              expanded={currentExamStartTimestamp ? true : expanded}
                              onChange={handleAccordionChange}
                            >
                              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Typography>Часови интервали</Typography>
                              </AccordionSummary>
                              <AccordionDetails>
                                <ArrayInput
                                  source="timeSlots"
                                  defaultValue={[{}]}
                                  label=""
                                >
                                  <SimpleFormIterator
                                    disableAdd={isExamPassed || readOnly}
                                    disableRemove={isExamPassed || readOnly}
                                    disableReordering={isExamPassed || readOnly}
                                    sx={settings.style.iteratorWithoutGap}
                                  >
                                    <FormDataConsumer>
                                      {({ scopedFormData }) => {
                                        return (
                                          <Grid
                                            container
                                            spacing={1}
                                          >
                                            <Grid
                                              item
                                              xs={12}
                                              md={2}
                                            >
                                              <CustomTimeInput
                                                source="startTime"
                                                label="Начало на изпит"
                                                required={true}
                                                validate={() => timeSlotStartTimeValidate(scopedFormData, examDayData)}
                                                defaultDateValue={currentExamStartTimestamp}
                                                readOnly={isExamPassed || readOnly}
                                              />
                                            </Grid>
                                            <Grid
                                              item
                                              xs={12}
                                              md={2}
                                            >
                                              <CustomTimeInput
                                                source="endTime"
                                                label="Край на изпит"
                                                required={true}
                                                validate={() => timeSlotEndTimeValidate(scopedFormData, examDayData)}
                                                defaultDateValue={currentExamStartTimestamp}
                                                readOnly={readOnly}
                                              />
                                            </Grid>
                                            {examCategoryId === 2 && (
                                              <>
                                                <Grid
                                                  item
                                                  xs={12}
                                                  md={2}
                                                >
                                                  <NumberInput
                                                    source="capacity"
                                                    label="Брой студенти на час"
                                                    defaultValue={0}
                                                    validate={required()}
                                                    readOnly={isExamPassed || readOnly}
                                                  />
                                                </Grid>
                                                <Grid
                                                  item
                                                  xs={12}
                                                  md={2}
                                                >
                                                  <CustomTimeInput
                                                    source="breakHourStart"
                                                    label="Начало на почивка"
                                                    required={false}
                                                    validate={() => timeSlotBreakHoursValidate(scopedFormData)}
                                                    defaultDateValue={currentExamStartTimestamp}
                                                    readOnly={isExamPassed || readOnly}
                                                  />
                                                </Grid>
                                                <Grid
                                                  item
                                                  xs={12}
                                                  md={2}
                                                >
                                                  <CustomTimeInput
                                                    source="breakHourEnd"
                                                    label="Край на почивка"
                                                    required={false}
                                                    validate={() => timeSlotBreakHoursValidate(scopedFormData)}
                                                    defaultDateValue={currentExamStartTimestamp}
                                                    readOnly={isExamPassed || readOnly}
                                                  />
                                                </Grid>
                                              </>
                                            )}
                                            <Grid
                                              item
                                              xs={12}
                                              md={2}
                                            >
                                              <BooleanInput
                                                source="isBackup"
                                                label="Резервен час"
                                                readOnly={isExamPassed || readOnly}
                                                sx={{ padding: 1 }}
                                              />
                                            </Grid>
                                            {examCategoryId !== ExamCategories.DocumentBased &&
                                              examTypeId !== ExamTypes.Remote && (
                                                <>
                                                  <Grid
                                                    item
                                                    xs={12}
                                                    md={2}
                                                  >
                                                    <BooleanInput
                                                      source="isRoomsChanges"
                                                      label="Промени зали"
                                                      readOnly={isExamPassed || readOnly}
                                                      sx={{ padding: 1 }}
                                                    />
                                                  </Grid>
                                                  {scopedFormData?.isRoomsChanges ? (
                                                    <RoomsList
                                                      record={record}
                                                      readOnly={readOnly}
                                                    />
                                                  ) : null}
                                                </>
                                              )}
                                          </Grid>
                                        );
                                      }}
                                    </FormDataConsumer>
                                  </SimpleFormIterator>
                                </ArrayInput>
                              </AccordionDetails>
                            </Accordion>
                          </Grid>
                        )}
                        <BooleanInput
                          source="isBackup"
                          label="Резервна дата"
                          readOnly={isExamPassed || readOnly}
                        />
                      </CardContent>
                    </Card>
                  );
                }}
              </FormDataConsumer>
            </SimpleFormIterator>
          </ArrayInput>
        </Grid>
      </Grid>
    </>
  );
};

export default ExamsSlots;
